import { FormEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Camera, LocateFixed, MapPin, Play, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import { executeOrQueue } from "@/offline/executeOrQueue";
import { queueMediaUpload } from "@/offline/outbox";
import { useOffline } from "@/offline/OfflineProvider";
import { toApiId } from "@/services/http/mappers";
import type { FarmDetail, VisitActivityDefinition, VisitPlan, VisitReason } from "@/types/domain";
import { captureCurrentPosition, saveFieldVisitContext } from "@/features/visits/fieldVisitLocal";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function VisitStartPage() {
  const { planId = "", targetId = "" } = useParams();
  const navigate = useNavigate();
  const offline = useOffline();
  const [plan, setPlan] = useState<VisitPlan>();
  const [farm, setFarm] = useState<FarmDetail>();
  const [reasons, setReasons] = useState<VisitReason[]>([]);
  const [activityCatalogue, setActivityCatalogue] = useState<VisitActivityDefinition[]>([]);
  const [selectedPlotIds, setSelectedPlotIds] = useState<string[]>([]);
  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>([]);
  const [checkInPhoto, setCheckInPhoto] = useState<File>();
  const [photoCapturedAt, setPhotoCapturedAt] = useState("");
  const [observations, setObservations] = useState("");
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [gpsMessage, setGpsMessage] = useState("GPS will be captured when you check in.");

  useEffect(() => {
    let active = true;
    services.visits.getPlan(planId)
      .then(async (record) => {
        if (!active) return;
        if (!record) throw new Error("Visit Plan was not found.");
        const target = record.targets.find((item) => item.id === targetId);
        if (!target) throw new Error("This farmer is not part of the Visit Plan.");
        const [farmRecord, reasonRows, activityRows] = await Promise.all([
          services.farms.get(target.farmId),
          services.visits.listReasons(),
          services.visits.listActivityCatalogue(),
        ]);
        if (!active) return;
        if (!farmRecord) throw new Error("The target Farm was not found.");
        setPlan(record);
        setFarm(farmRecord);
        setReasons(reasonRows);
        setActivityCatalogue(activityRows);
      })
      .catch((reason: Error) => {
        if (active) setError(reason.message || "Field check-in could not be prepared.");
      });
    return () => { active = false; };
  }, [planId, targetId]);

  const target = plan?.targets.find((item) => item.id === targetId);
  const activePlots = useMemo(
    () => (farm?.plotsData || []).filter((plot) => plot.status === "ACTIVE"),
    [farm],
  );

  const togglePlot = (plotId: string, checked: boolean) => {
    setSelectedPlotIds((current) =>
      checked ? Array.from(new Set([...current, plotId])) : current.filter((id) => id !== plotId),
    );
  };

  const toggleReason = (reasonId: string, checked: boolean) => {
    setSelectedReasonIds((current) =>
      checked ? Array.from(new Set([...current, reasonId])) : current.filter((id) => id !== reasonId),
    );
  };

  const start = async (event: FormEvent) => {
    event.preventDefault();
    if (!plan || !target || !farm) return;
    if (selectedPlotIds.length === 0) {
      toast.error("Select at least one Plot actually being visited.");
      return;
    }
    if (selectedReasonIds.length === 0) {
      toast.error("Select at least one reason for the visit.");
      return;
    }
    if (!checkInPhoto) {
      toast.error("A check-in photo is required before starting the visit.");
      return;
    }

    setWorking(true);
    try {
      setGpsMessage("Capturing precise GPS position...");
      const position = await captureCurrentPosition();
      setGpsMessage(`GPS captured · ±${Math.round(position.accuracyMetres)} m`);

      const visitId = crypto.randomUUID();
      const evidenceId = crypto.randomUUID();
      const startedAt = new Date().toISOString();
      const capturedAt = photoCapturedAt || startedAt;
      const isOffline = offline.simulatedOffline || !offline.online;
      const selectedPlots = activePlots.filter((plot) => selectedPlotIds.includes(plot.id));
      const selectedReasons = reasons.filter((reason) => selectedReasonIds.includes(reason.id));

      const startInput = {
        clientGeneratedId: visitId,
        plotIds: selectedPlotIds,
        reasonIds: selectedReasonIds,
        startedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gpsAccuracyMetres: position.accuracyMetres,
        deviceIdentifier: offline.deviceId,
        createdOffline: isOffline,
        localCreatedAt: startedAt,
        observations: observations.trim() || undefined,
      };

      saveFieldVisitContext({
        visitId,
        planId,
        targetId,
        farmerId: target.farmerId,
        farmerName: target.farmerName || "Farmer",
        farmId: target.farmId,
        farmName: target.farmName || farm.name,
        plotIds: selectedPlotIds,
        plotNames: selectedPlots.map((plot) => plot.name),
        assignedStaffName: plan.assignedStaffName,
        reasonIds: selectedReasonIds,
        reasonNames: selectedReasons.map((reason) => reason.reasonName),
        activityCatalogue,
        startedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gpsAccuracyMetres: position.accuracyMetres,
        evidenceTypes: ["CHECK_IN"],
        activityCount: 0,
        followUpCount: 0,
        observations: observations.trim() || undefined,
      });

      const apiPayload = {
        client_generated_id: visitId,
        plot_ids: selectedPlotIds.map(toApiId),
        reason_ids: selectedReasonIds.map(toApiId),
        started_at: startedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gps_accuracy_metres: position.accuracyMetres,
        device_identifier: offline.deviceId,
        created_offline: isOffline,
        local_created_at: startedAt,
        observations: observations.trim() || undefined,
      };

      const result = await executeOrQueue({
        offline: isOffline,
        request: {
          entityType: "FIELD_VISIT_START",
          entityId: visitId,
          method: "POST",
          path: `/visit-plans/${toApiId(planId)}/targets/${toApiId(targetId)}/start`,
          payload: apiPayload,
        },
        execute: () => services.visits.startPlanned(planId, targetId, startInput, visitId),
      });

      const evidenceQuery = {
        captured_at: capturedAt,
        latitude: position.latitude,
        longitude: position.longitude,
        gps_accuracy_metres: position.accuracyMetres,
        plot_id: selectedPlotIds[0] ? toApiId(selectedPlotIds[0]) : undefined,
        device_identifier: offline.deviceId,
        created_offline: isOffline,
        local_created_at: capturedAt,
        client_generated_id: evidenceId,
      };

      if (result.mode === "QUEUED") {
        await queueMediaUpload({
          id: evidenceId,
          entityType: "FIELD_VISIT_EVIDENCE",
          entityId: visitId,
          path: `/visits/${toApiId(visitId)}/evidence/CHECK_IN`,
          file: checkInPhoto,
          query: evidenceQuery,
        });
        toast.success("Field visit checked in offline", {
          description: "The visit and its GPS/photo evidence are safely queued for synchronization.",
        });
      } else {
        try {
          await services.visits.uploadEvidence(visitId, {
            clientGeneratedId: evidenceId,
            evidenceType: "CHECK_IN",
            file: checkInPhoto,
            capturedAt,
            latitude: position.latitude,
            longitude: position.longitude,
            gpsAccuracyMetres: position.accuracyMetres,
            plotId: selectedPlotIds[0],
            deviceIdentifier: offline.deviceId,
            createdOffline: false,
            localCreatedAt: capturedAt,
          });
        } catch (uploadError) {
          if (!navigator.onLine) {
            await queueMediaUpload({
              id: evidenceId,
              entityType: "FIELD_VISIT_EVIDENCE",
              entityId: visitId,
              path: `/visits/${toApiId(visitId)}/evidence/CHECK_IN`,
              file: checkInPhoto,
              query: evidenceQuery,
            });
            toast("Visit started; photo queued", {
              description: "The connection dropped after check-in. Evidence will upload when connectivity returns.",
            });
          } else {
            throw uploadError;
          }
        }
        toast.success("Field visit started", {
          description: `GPS and check-in evidence recorded for ${selectedPlotIds.length} selected Plot${selectedPlotIds.length === 1 ? "" : "s"}.`,
        });
      }

      navigate(`/visits/${visitId}/execute`);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Field check-in failed.";
      setGpsMessage(message);
      toast.error(message);
    } finally {
      setWorking(false);
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!plan || !farm || !target) return <LoadingState label="Preparing field check-in" />;

  return (
    <div className="space-y-6" data-testid="visit-start-page">
      <PageHeader
        eyebrow="Field Visit · Check-in"
        title={target.farmerName || "Farmer visit"}
        description={`${target.farmName || farm.name} · ${plan.title}`}
        backTo={`/visits/plans/${plan.id}`}
      />

      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={target.status} testId="visit-start-target-status" />
        <span className="text-sm text-muted-foreground">
          Assigned to <span className="font-semibold text-foreground">{plan.assignedStaffName || "field staff"}</span>
        </span>
      </div>

      <form onSubmit={start} className="space-y-5">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 size-5 text-primary" />
              <div>
                <p className="font-semibold">1. Select Plot(s) actually being visited</p>
                <p className="mt-1 text-sm text-muted-foreground">Do not select a Plot that you will not physically inspect. Unselected Plots remain unvisited.</p>
              </div>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {activePlots.map((plot) => (
                <label key={plot.id} className="flex cursor-pointer items-start gap-3 rounded-xl border p-3">
                  <input
                    type="checkbox"
                    className="mt-1 size-4"
                    checked={selectedPlotIds.includes(plot.id)}
                    onChange={(event) => togglePlot(plot.id, event.target.checked)}
                  />
                  <span>
                    <span className="font-medium">{plot.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {plot.code}{plot.localityName ? ` · ${plot.localityName}` : ""}{plot.reportedAreaAcres ? ` · ${plot.reportedAreaAcres} acres` : ""}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="font-semibold">2. Reason for visit</p>
            <p className="mt-1 text-sm text-muted-foreground">Select every purpose that applies. Activities performed will be recorded separately during the visit.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {reasons.map((reason) => (
                <label key={reason.id} className="flex cursor-pointer items-start gap-3 rounded-xl border p-3">
                  <input
                    type="checkbox"
                    className="mt-1 size-4"
                    checked={selectedReasonIds.includes(reason.id)}
                    onChange={(event) => toggleReason(reason.id, event.target.checked)}
                  />
                  <span>
                    <span className="font-medium">{reason.reasonName}</span>
                    {reason.description && <span className="block text-xs text-muted-foreground">{reason.description}</span>}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              <Label htmlFor="visit-start-note">Initial note</Label>
              <Textarea
                id="visit-start-note"
                value={observations}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setObservations(event.target.value)}
                placeholder="Optional note on arrival or immediate concern"
              />
            </div>
          </CardContent>
        </Card>

        <Card className={!checkInPhoto ? "border-warning/30" : "border-primary/20"}>
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Camera className="mt-0.5 size-5 text-primary" />
              <div className="flex-1">
                <p className="font-semibold">3. Mandatory check-in photo</p>
                <p className="mt-1 text-sm text-muted-foreground">Capture fresh field evidence on arrival. The original field time and GPS are retained even when the photo uploads later.</p>
                <Input
                  className="mt-4"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const file = event.target.files?.[0];
                    setCheckInPhoto(file);
                    setPhotoCapturedAt(file ? new Date().toISOString() : "");
                  }}
                  data-testid="visit-check-in-photo"
                />
                {checkInPhoto && <p className="mt-2 text-xs font-medium text-primary">{checkInPhoto.name} ready</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <LocateFixed className="mt-0.5 size-5 text-primary" />
              <div>
                <p className="font-semibold">4. GPS + timestamp check-in</p>
                <p className="text-sm text-muted-foreground">{gpsMessage}</p>
              </div>
            </div>
            <Button type="submit" disabled={working || activePlots.length === 0} data-testid="visit-check-in-button">
              {working ? <LocateFixed className="mr-2 size-4 animate-pulse" /> : <Play className="mr-2 size-4" />}
              {working ? "Capturing check-in..." : "Check in & start visit"}
            </Button>
          </CardContent>
        </Card>

        {(offline.simulatedOffline || !offline.online) && (
          <Card className="border-warning/30 bg-warning/[0.03]">
            <CardContent className="flex items-start gap-3 p-4 text-sm">
              <ShieldCheck className="mt-0.5 size-4 text-warning" />
              <p><span className="font-semibold">Offline mode:</span> visit details and the photo will remain on this device and synchronize when connectivity returns.</p>
            </CardContent>
          </Card>
        )}
      </form>
    </div>
  );
}
